import React, { ReactNode, useEffect, useState } from 'react';

import { Alert, Button, ConfigProvider, Layout, Space, theme } from 'antd';

import { FileDoneOutlined } from '@ant-design/icons';
import { SupportButton } from 'components/_Common/SupportButton';
import { ClientManagerPendingConsentModal } from 'components/ClientManager/PendingConsent/ClientManagerPendingConsentModal';
import { COPYRIGHT, GCONDO_BROKER_THEME } from 'internal-constants';
import Authentication from 'lib/Authentication';
import { hasServiceError } from 'lib/helpers/ServiceHelper';
import { Show } from 'lib/Show';
import { listAllDocumentsOverdueOrToExpireInNext60Days } from 'services/Overview.service';

import { expiredTrial } from '../MainLayout';
import { SecondaryHeader } from './SecondaryHeader';
import { SecondarySider } from './SecondarySider';

type Props = { children: ReactNode };

export function SecondaryLayout({ children }: Props) {
    const [hasExpiringInsurance, setHasExpiringInsurance] = useState(false);

    const [isPendingConsentModalVisible, setIsConsentModalVisible] = useState(true);

    const { token } = theme.useToken();

    useEffect(() => {
        const fetchExpiringInsurance = async () => {
            const response = await listAllDocumentsOverdueOrToExpireInNext60Days();

            if (hasServiceError(response))
                return;
    
            const { documents } = response;

            const filteredDocuments = documents.filter(({ document_type: type }) => type.name === 'Apólice de seguro');

            setHasExpiringInsurance(filteredDocuments.length > 0);
        };

        fetchExpiringInsurance();
    }, []);

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    const contentStyle = {
        margin: isMobile ? '0px 10px' : '0px 16px',
        padding: isMobile ? 'unset' : '10px 24px',
        minHeight: 280
    };

    const { consent } = Authentication.getClientManager();

    const action = (
        <Button 
            type="primary"
            onClick={() => setIsConsentModalVisible(true)}
        >
            Quero saber mais sobre
        </Button>
    );

    const isConsentAlertVisible = consent === null && hasExpiringInsurance;

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <SecondarySider />

            <Layout>
                <SecondaryHeader />

                {expiredTrial()}

                <Show when={isConsentAlertVisible}>
                    <ConfigProvider theme={GCONDO_BROKER_THEME}>
                        <Alert
                            message={(
                                <span style={{ fontWeight: token.fontWeightStrong }}>
                                Evite ficar sem cobertura! Autorize e receba cotações
                                </span>
                            )}
                            description={(
                                <Space direction="vertical">
                                    Autorize uma única vez o compartilhamento e passe a receber cotações de corretores parceiros com antecedência. Assim você terá tempo suficiente para analisar as melhores propostas e decidir com tranquilidade qual é a melhor opção para os seus condomínios.
                                    {action}
                                </Space>
                            )}
                            type="info"
                            icon={<FileDoneOutlined />}
                            banner
                            showIcon
                        />
                    </ConfigProvider>
                </Show>

                <Layout.Content style={contentStyle}>
                    {children}
                </Layout.Content>

                <Layout.Footer style={{ textAlign: 'center' }}>
                    {COPYRIGHT}
                </Layout.Footer>
            </Layout>

            <Show when={isConsentAlertVisible && isPendingConsentModalVisible}>
                <ClientManagerPendingConsentModal
                    onCancel={() => setIsConsentModalVisible(false)}
                />
            </Show>

            <SupportButton />
        </Layout>
    );
}
